import React, { useEffect, useState } from 'react';
import { RetentionQueue } from '../components/RetentionQueue';
import { RetentionActionModal } from '../components/RetentionActionModal';
import { Loan, RetentionOutcome } from '../types';
// MOCK DATA GENERATION
const generateMockData = () => {
  const activeLoans: Loan[] = [{
    id: '1',
    loanNumber: 'LN-2024-83921',
    borrowerName: 'Sarah Jenkins',
    borrowerPhone: '(512) 555-0147',
    loanOfficer: 'Michael Scott',
    pushedBy: 'Dwight Schrute',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    retentionComment: 'Borrower unresponsive - 3 attempts made',
    status: 'Active',
    amount: 450000
  }, {
    id: '2',
    loanNumber: 'LN-2024-83925',
    borrowerName: 'Robert California',
    borrowerPhone: '(310) 555-0293',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Andy Bernard',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    retentionComment: 'Rate lock expired - borrower reconsidering',
    status: 'Active',
    amount: 620000
  }, {
    id: '3',
    loanNumber: 'LN-2024-83930',
    borrowerName: 'Stanley Hudson',
    borrowerPhone: '(215) 555-0184',
    loanOfficer: 'Phyllis Vance',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
    retentionComment: 'Missing income documentation for co-borrower',
    status: 'Active',
    amount: 325000
  }, {
    id: '4',
    loanNumber: 'LN-2024-83942',
    borrowerName: 'Kelly Kapoor',
    borrowerPhone: '(469) 555-0321',
    loanOfficer: 'Ryan Howard',
    pushedBy: 'Toby Flenderson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    retentionComment: 'Appraisal came in low - contesting value',
    status: 'Active',
    amount: 290000
  }, {
    id: '5',
    loanNumber: 'LN-2024-83955',
    borrowerName: 'Kevin Malone',
    borrowerPhone: '(602) 555-0178',
    loanOfficer: 'Oscar Martinez',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    retentionComment: 'DTI ratio exceeds guidelines after credit refresh',
    status: 'Active',
    amount: 410000
  }, {
    id: '6',
    loanNumber: 'LN-2024-83961',
    borrowerName: 'Meredith Palmer',
    borrowerPhone: '(813) 555-0256',
    loanOfficer: 'Creed Bratton',
    pushedBy: 'Pam Beesly',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
    retentionComment: 'Title issues discovered on property history',
    status: 'Active',
    amount: 185000
  }, {
    id: '7',
    loanNumber: 'LN-2024-83977',
    borrowerName: 'Erin Hannon',
    borrowerPhone: '(720) 555-0199',
    loanOfficer: 'Andy Bernard',
    pushedBy: 'Gabe Lewis',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 0.5).toISOString(),
    retentionComment: 'Employment verification failed',
    status: 'Active',
    amount: 340000
  }, {
    id: '8',
    loanNumber: 'LN-2024-83989',
    borrowerName: 'Darryl Philbin',
    borrowerPhone: '(404) 555-0342',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Val Johnson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    retentionComment: 'Borrower requested hold due to family emergency',
    status: 'Active',
    amount: 515000
  }];
  return {
    activeLoans
  };
};
const CURRENT_USER = 'Jane Cooper';
export function RetentionCenter() {
  const [data, setData] = useState(generateMockData());
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Auto-release expired claims (older than 1 hour)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      setData(prev => {
        let hasChanges = false;
        const updatedLoans = prev.activeLoans.map(loan => {
          if (loan.claimedAt) {
            const claimTime = new Date(loan.claimedAt).getTime();
            if (now - claimTime >= oneHour) {
              hasChanges = true;
              return {
                ...loan,
                claimedBy: undefined,
                claimedAt: undefined
              };
            }
          }
          return loan;
        });
        return hasChanges ? {
          ...prev,
          activeLoans: updatedLoans
        } : prev;
      });
    }, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);
  const handleClaim = (loanId: string) => {
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.map(loan => loan.id === loanId ? {
        ...loan,
        claimedBy: CURRENT_USER,
        claimedAt: new Date().toISOString()
      } : loan)
    }));
  };
  const handleRelease = (loanId: string) => {
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.map(loan => loan.id === loanId ? {
        ...loan,
        claimedBy: undefined,
        claimedAt: undefined
      } : loan)
    }));
  };
  const handleActionClick = (loan: Loan) => {
    if (loan.claimedBy === CURRENT_USER) {
      setSelectedLoan(loan);
      setIsModalOpen(true);
    }
  };
  const handleActionComplete = (loanId: string, outcome: RetentionOutcome, notes: string) => {
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.filter(l => l.id !== loanId)
    }));
  };
  return <div className="min-h-screen bg-white font-sans text-black" data-id="element-108">
      <main className="px-3 py-3" data-id="element-109">
        <div data-id="element-110">
          <div className="flex items-center gap-2.5 mb-3" data-id="element-111">
            <div className="w-1 h-5 rounded-full bg-accent-500" data-id="element-112" />
            <h2 className="text-sm font-bold text-brand-500 uppercase tracking-wider" data-id="element-113">
              Retention Center
            </h2>
            <span className="text-[11px] font-medium text-accent-600 bg-accent-50 border border-accent-100 rounded-full px-2 py-0.5" data-id="element-114">
              {data.activeLoans.length} active
            </span>
          </div>
          <RetentionQueue loans={data.activeLoans} onActionClick={handleActionClick} onClaim={handleClaim} onRelease={handleRelease} currentUser={CURRENT_USER} data-id="element-115" />
        </div>
      </main>

      <RetentionActionModal loan={selectedLoan} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAction={handleActionComplete} data-id="element-116" />
    </div>;
}